import type { StoredPatch } from "../types.js";

export interface AccumulatorEntry {
    readonly content: string;
    readonly baseGeneration: string;
}

/**
 * Inter-patch THEIRS coordination across `applyPatches()`.
 *
 * When two patches modify the same file, the second patch's 3-way merge has
 * to know about the first patch's edits — otherwise it overwrites them. The
 * accumulator threads each patch's computed THEIRS by resolved file path so
 * subsequent patches can use it as a merge base / pre-merge.
 *
 * # API
 *
 * - `isShared(path)` — is this file touched by 2+ patches in this run?
 * - `has(path)` / `lookup(path)` — has any prior patch already stored a
 *   result for this path? Returns the stored entry.
 * - `shouldSkipFastPath(patch)` — true if any of the patch's files have
 *   prior accumulator entries; the fast path (`git apply --3way`) cannot
 *   see accumulator state, so we route through the slow path.
 * - `recordMerge(path, content, baseGen)` — store a successful merge result.
 *   Always populates.
 * - `recordConflict(path, content, baseGen)` — store a conflicted merge
 *   result. Populates only in `"resolve"` mode (replay mode keeps the
 *   accumulator free of marker-laden content).
 * - `snapshot()` — defensive copy for test introspection (invariant I2).
 *
 * # Mode semantics (`recordConflict`)
 *
 * Replay mode strips conflict markers off-disk after the apply loop and
 * commits clean files; the accumulator must NOT carry marker-laden content
 * forward to subsequent patches. In resolve mode the customer is going to
 * see and edit markers, so storing them lets a later patch use the
 * conflicted file as a structurally-correct merge base.
 *
 * # Lifecycle
 *
 * One accumulator instance per `applyPatches()` invocation. Constructed
 * with the run's apply mode and the precomputed set of shared files. The
 * instance is single-use — tests that want to inspect post-run state call
 * `snapshot()`, then the next `applyPatches()` constructs a fresh one.
 */
export class MergeAccumulator {
    private readonly entries = new Map<string, AccumulatorEntry>();

    constructor(
        private readonly mode: "replay" | "resolve",
        private readonly sharedFiles: ReadonlySet<string>,
    ) {}

    /** Was this file touched by 2+ patches in the current `applyPatches` invocation? */
    isShared(path: string): boolean {
        return this.sharedFiles.has(path);
    }

    /** Has any prior patch already recorded a result for this path? */
    has(path: string): boolean {
        return this.entries.has(path);
    }

    /** Look up the accumulated content + base generation for this path. */
    lookup(path: string): AccumulatorEntry | undefined {
        return this.entries.get(path);
    }

    /**
     * True if any of the patch's files have a prior accumulator entry — in
     * which case the fast path (`git apply --3way`) cannot honor prior
     * patches' contributions and we must route through the slow path.
     */
    shouldSkipFastPath(patch: StoredPatch): boolean {
        return patch.files.some((f) => this.entries.has(f));
    }

    /** Record a successful (clean) merge result. */
    recordMerge(path: string, content: string, baseGeneration: string): void {
        this.entries.set(path, { content, baseGeneration });
    }

    /**
     * Record a conflicted merge result. Populates only in `"resolve"` mode
     * — replay mode keeps the accumulator clean of marker-laden content
     * since markers will be stripped from disk after the apply loop.
     */
    recordConflict(path: string, content: string, baseGeneration: string): void {
        if (this.mode === "resolve") {
            this.entries.set(path, { content, baseGeneration });
        }
    }

    /**
     * Defensive copy for test introspection. Used by invariant I2 to assert
     * that after every applied patch, the accumulator has an entry for each
     * file the patch touched (see `__tests__/invariants/i2-accumulator-correctness.ts`).
     *
     * @internal
     */
    snapshot(): ReadonlyMap<string, AccumulatorEntry> {
        const copy = new Map<string, AccumulatorEntry>();
        for (const [path, entry] of this.entries) {
            copy.set(path, { content: entry.content, baseGeneration: entry.baseGeneration });
        }
        return copy;
    }
}
